'use client';

import { ChangeEvent, ReactNode, useEffect, useRef, useState, useTransition } from 'react';
import { Locale, useRouter } from '@/i18n/routing';
import clsx from 'clsx';

type Props = {
  children: ReactNode;
  defaultValue: string;
};

export const LocaleSwitcherSelect = ({ children, defaultValue }: Props) => {
  const router = useRouter();
  const [isPending, startTransition] = useTransition();
  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef<HTMLSelectElement>(null);

  const onSelectChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const nextLocale = event.target.value as Locale;
    startTransition(() => {
      router.push({ pathname: '/'}, { locale: nextLocale })
    });
  };

  const handleClick = () => {
    setIsOpen(!isOpen);
  };

  // if select is open and is closed by clicking outside of it or ESC key is pressed, set isOpen to false and blur the select
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        selectRef.current?.blur();
        setIsOpen(false);
      }
    };

    const handleClickOutside = (event: MouseEvent) => {
      if (event.target instanceof HTMLElement && !event.target.closest('.language-switch')) {
        selectRef.current?.blur();
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <select
    ref={selectRef}
    onClick={handleClick}
    id="lang-switcher-select"
    name="lang-switcher-select"
    className={clsx('lang-select', {'is-open': isOpen })}
    defaultValue={defaultValue}
    disabled={isPending}
    onChange={onSelectChange}>
      {children}
    </select>
  );
}